import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthGate from './AuthGate';
import AccountHeader from './AccountHeader';
import '../static/css/SettingsPage.css';
import '../static/css/ChangePassword.css';

const eyeIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const eyeOffIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const checkIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const dotIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="3" />
    </svg>
);

class ChangePassword extends Component {
    constructor() {
        super();
        const userJson = localStorage.getItem('user');
        const user = userJson ? JSON.parse(userJson) : null;
        this.state = {
            providerType: (user && user.provider_type) || null,
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
            showCurrent: false,
            showNew: false,
            showConfirm: false,
            submitting: false,
            error: '',
            showSuccessModal: false,
        };
        this.onChangeCurrentPassword = this.onChangeCurrentPassword.bind(this);
        this.onChangeNewPassword = this.onChangeNewPassword.bind(this);
        this.onChangeConfirmNewPassword = this.onChangeConfirmNewPassword.bind(this);
        this.toggleShowCurrent = this.toggleShowCurrent.bind(this);
        this.toggleShowNew = this.toggleShowNew.bind(this);
        this.toggleShowConfirm = this.toggleShowConfirm.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.closeSuccessModal = this.closeSuccessModal.bind(this);
    }

    onChangeCurrentPassword(e) {
        this.setState({ currentPassword: e.target.value, error: '' });
    }

    onChangeNewPassword(e) {
        this.setState({ newPassword: e.target.value, error: '' });
    }

    onChangeConfirmNewPassword(e) {
        this.setState({ confirmNewPassword: e.target.value, error: '' });
    }

    toggleShowCurrent() {
        this.setState((prev) => ({ showCurrent: !prev.showCurrent }));
    }

    toggleShowNew() {
        this.setState((prev) => ({ showNew: !prev.showNew }));
    }

    toggleShowConfirm() {
        this.setState((prev) => ({ showConfirm: !prev.showConfirm }));
    }

    closeSuccessModal() {
        this.setState({ showSuccessModal: false });
    }

    getRuleChecks() {
        const { newPassword, confirmNewPassword, currentPassword } = this.state;
        return {
            length: newPassword.length >= 8 && newPassword.length <= 64,
            number: /\d/.test(newPassword),
            special: /^(?=.*[^a-zA-Z0-9])[\x21-\x7E]+$/.test(newPassword),
            differs: newPassword.length > 0 && newPassword !== currentPassword,
            matches: newPassword.length > 0 && newPassword === confirmNewPassword,
        };
    }

    isFormValid() {
        const { currentPassword } = this.state;
        if (currentPassword.length === 0) return false;
        const checks = this.getRuleChecks();
        return checks.length && checks.number && checks.special && checks.differs && checks.matches;
    }

    async onSubmit(e) {
        e.preventDefault();
        if (!this.isFormValid() || this.state.submitting) return;
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ error: 'Your session has expired. Please sign in again.' });
            return;
        }
        this.setState({ submitting: true, error: '' });
        try {
            await axios.post(
                process.env.REACT_APP_SERVER_API + '/profile/password-update',
                {
                    current_password: this.state.currentPassword,
                    new_password: this.state.newPassword,
                    confirm_new_password: this.state.confirmNewPassword,
                },
                { headers: { Authorization: 'Bearer ' + token } }
            );
            this.setState({
                submitting: false,
                showSuccessModal: true,
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: '',
                showCurrent: false,
                showNew: false,
                showConfirm: false,
                error: '',
            });
        } catch (err) {
            const status = err.response && err.response.status;
            const data = err.response && err.response.data;
            const serverError = data && (data.error || (typeof data === 'string' ? data : ''));
            let message = 'Something went wrong. Please try again.';
            if (status === 401) {
                message = 'Current password is incorrect.';
            } else if ((status === 400 || status === 403) && serverError) {
                message = serverError;
            }
            this.setState({ submitting: false, error: message });
        }
    }

    renderProviderLocked() {
        const { providerType } = this.state;
        const providerLabel = providerType === 'APPLE' ? 'Apple' : 'Google';
        return (
            <div className="change-password-locked">
                <p className="change-password-locked-message">
                    Your account was created with <span className="change-password-locked-provider">{providerLabel}</span> Sign In, so there's no AniMates password to change here.
                    To update your sign-in credentials, manage them directly with {providerLabel}.
                </p>
                <p className="change-password-locked-secondary">
                    Want to switch to email + password sign-in instead? We'll be adding that option soon.
                </p>
            </div>
        );
    }

    renderRulesChecklist() {
        const checks = this.getRuleChecks();
        const items = [
            { key: 'length', label: '8–64 characters', ok: checks.length },
            { key: 'number', label: 'Contains a number', ok: checks.number },
            { key: 'special', label: 'Contains a special character (any symbol except spaces)', ok: checks.special },
            { key: 'differs', label: 'Different from current password', ok: checks.differs },
            { key: 'matches', label: 'Matches confirm password', ok: checks.matches },
        ];
        return (
            <ul className="change-password-rules">
                {items.map((item) => (
                    <li key={item.key} className={`change-password-rule ${item.ok ? 'ok' : ''}`}>
                        <span className="change-password-rule-icon">{item.ok ? checkIcon : dotIcon}</span>
                        <span className="change-password-rule-label">{item.label}</span>
                    </li>
                ))}
            </ul>
        );
    }

    renderForm() {
        const { showCurrent, showNew, showConfirm, submitting, currentPassword, newPassword, confirmNewPassword, error } = this.state;
        const valid = this.isFormValid();
        const buttonDisabled = !valid || submitting;
        return (
            <form className="change-password-form" onSubmit={this.onSubmit}>
                <label className="change-password-form-label" htmlFor="change-password-current">Current password</label>
                <div className="change-password-form-input-wrapper">
                    <input
                        id="change-password-current"
                        type={showCurrent ? 'text' : 'password'}
                        className="change-password-form-input"
                        value={currentPassword}
                        onChange={this.onChangeCurrentPassword}
                        autoComplete="current-password"
                    />
                    <button
                        type="button"
                        className="change-password-form-toggle-password"
                        onClick={this.toggleShowCurrent}
                        aria-label={showCurrent ? 'Hide password' : 'Show password'}
                    >
                        {showCurrent ? eyeIcon : eyeOffIcon}
                    </button>
                </div>

                <label className="change-password-form-label" htmlFor="change-password-new">New password</label>
                <div className="change-password-form-input-wrapper">
                    <input
                        id="change-password-new"
                        type={showNew ? 'text' : 'password'}
                        className="change-password-form-input"
                        value={newPassword}
                        onChange={this.onChangeNewPassword}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        className="change-password-form-toggle-password"
                        onClick={this.toggleShowNew}
                        aria-label={showNew ? 'Hide password' : 'Show password'}
                    >
                        {showNew ? eyeIcon : eyeOffIcon}
                    </button>
                </div>
                {this.renderRulesChecklist()}

                <label className="change-password-form-label" htmlFor="change-password-confirm">Confirm new password</label>
                <div className="change-password-form-input-wrapper">
                    <input
                        id="change-password-confirm"
                        type={showConfirm ? 'text' : 'password'}
                        className="change-password-form-input"
                        value={confirmNewPassword}
                        onChange={this.onChangeConfirmNewPassword}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        className="change-password-form-toggle-password"
                        onClick={this.toggleShowConfirm}
                        aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    >
                        {showConfirm ? eyeIcon : eyeOffIcon}
                    </button>
                </div>

                {error && <p className="change-password-error">{error}</p>}
                <button type="submit" className="change-password-form-submit" disabled={buttonDisabled}>
                    {submitting ? 'Updating…' : 'Update Password'}
                </button>
            </form>
        );
    }

    renderSuccessModal() {
        return (
            <div
                className="change-password-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="change-password-modal-title"
            >
                <div className="change-password-modal-card">
                    <h3 id="change-password-modal-title" className="change-password-modal-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Password Updated</h3>
                    <p className="change-password-success">
                        Your password was successfully updated. Use your new password the next time you sign in.
                        We sent a confirmation to your email — if you didn't make this change, contact us right away.
                    </p>
                    <button
                        type="button"
                        className="change-password-modal-okay"
                        onClick={this.closeSuccessModal}
                        autoFocus
                    >
                        Okay
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { providerType } = this.state;
        const isProviderLocked = providerType === 'APPLE' || providerType === 'GOOGLE';
        return (
            <AuthGate>
                <div className="settings-page">
                    <AccountHeader />
                    <div className="settings-page-content change-password-content">
                        <Link to="/home" className="settings-back-link">Back</Link>
                        <h2 className="settings-page-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}><u>Change Password</u></h2>
                        {isProviderLocked ? this.renderProviderLocked() : this.renderForm()}
                    </div>
                    {this.state.showSuccessModal && this.renderSuccessModal()}
                </div>
            </AuthGate>
        );
    }
}

export default ChangePassword;
